import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'react-hot-toast';
import { 
  Plus, Edit, Trash2, Eye, EyeOff, X, Save, 
  Search, Filter, Star, StarOff, GripVertical,
  Shield, TrendingUp, Building2, Lock, Award, Cpu,
  ChevronDown, RotateCcw
} from 'lucide-react';
import { servicesService } from '../../services/api';
import LoadingSpinner from '../../components/ui/LoadingSpinner';

const iconOptions = [
  { value: 'strategy', label: 'Stratégie', icon: Shield },
  { value: 'wealth', label: 'Wealth', icon: TrendingUp },
  { value: 'banking', label: 'Banque', icon: Building2 },
  { value: 'compliance', label: 'Conformité', icon: Lock },
  { value: 'education', label: 'Éducation', icon: Award },
  { value: 'support', label: 'Support', icon: Cpu },
];

const iconMap = {
  strategy: Shield,
  wealth: TrendingUp,
  banking: Building2,
  compliance: Lock,
  education: Award,
  support: Cpu,
};

function ServicesPage() {
  const [services, setServices] = useState([]);
  const [filteredServices, setFilteredServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  
  // Filtres
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all'); // all, active, inactive
  const [filterFeatured, setFilterFeatured] = useState('all'); // all, featured, not_featured
  const [sortBy, setSortBy] = useState('order'); // order, title, date
  const [showFilters, setShowFilters] = useState(false);

  const [formData, setFormData] = useState({
    title: '',
    short_description: '',
    description: '',
    icon: 'strategy',
    image_url: '',
    features: [''],
    is_featured: false,
    is_active: true,
    display_order: 0
  });

  useEffect(() => {
    loadServices();
  }, []);

  // Appliquer les filtres quand les services ou les filtres changent
  useEffect(() => {
    applyFilters();
  }, [services, searchTerm, filterStatus, filterFeatured, sortBy]);

  const loadServices = async () => {
    try {
      const response = await servicesService.getAll({ active: 'false' });
      setServices(response.data.services || []);
    } catch (error) {
      console.error('Erreur:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let result = [...services];

    // Recherche par titre ou description
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(service => 
        service.title?.toLowerCase().includes(term) ||
        service.short_description?.toLowerCase().includes(term) ||
        service.description?.toLowerCase().includes(term)
      );
    }

    // Filtre par statut (actif/inactif)
    if (filterStatus === 'active') {
      result = result.filter(service => service.is_active);
    } else if (filterStatus === 'inactive') {
      result = result.filter(service => !service.is_active);
    }

    // Filtre par mis en avant
    if (filterFeatured === 'featured') {
      result = result.filter(service => service.is_featured);
    } else if (filterFeatured === 'not_featured') {
      result = result.filter(service => !service.is_featured);
    }

    // Tri
    if (sortBy === 'title') {
      result.sort((a, b) => a.title?.localeCompare(b.title));
    } else if (sortBy === 'date') {
      result.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    } else {
      result.sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
    }

    setFilteredServices(result);
  };

  const resetFilters = () => {
    setSearchTerm('');
    setFilterStatus('all');
    setFilterFeatured('all');
    setSortBy('order');
  };

  const activeFiltersCount = [
    searchTerm !== '',
    filterStatus !== 'all',
    filterFeatured !== 'all',
    sortBy !== 'order'
  ].filter(Boolean).length;

  const openModal = (service = null) => {
    if (service) {
      setEditing(service);
      setFormData({
        title: service.title || '',
        short_description: service.short_description || '',
        description: service.description || '',
        icon: service.icon || 'strategy',
        image_url: service.image_url || '',
        features: service.features?.length > 0 ? service.features : [''],
        is_featured: service.is_featured || false,
        is_active: service.is_active !== false,
        display_order: service.display_order || 0
      });
    } else {
      setEditing(null);
      setFormData({
        title: '',
        short_description: '',
        description: '',
        icon: 'strategy',
        image_url: '',
        features: [''],
        is_featured: false,
        is_active: true,
        display_order: services.length
      });
    }
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const dataToSend = {
      ...formData,
      features: formData.features.filter(f => f.trim() !== '')
    };

    try {
      if (editing) {
        await servicesService.update(editing.id, dataToSend);
        toast.success('Service mis à jour');
      } else {
        await servicesService.create(dataToSend);
        toast.success('Service créé');
      }
      setShowModal(false);
      loadServices();
    } catch (error) {
      console.error('Erreur:', error);
      toast.error('Erreur lors de l\'enregistrement');
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Supprimer ce service ?')) return;
    
    try {
      await servicesService.delete(id);
      toast.success('Service supprimé');
      loadServices();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const toggleActive = async (service) => {
    try {
      await servicesService.update(service.id, { is_active: !service.is_active });
      toast.success('Statut mis à jour');
      loadServices();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const toggleFeatured = async (service) => {
    try {
      await servicesService.update(service.id, { is_featured: !service.is_featured });
      toast.success('Mis en avant mis à jour');
      loadServices();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const addFeature = () => {
    setFormData(prev => ({ ...prev, features: [...prev.features, ''] }));
  };

  const removeFeature = (index) => {
    setFormData(prev => ({
      ...prev,
      features: prev.features.filter((_, i) => i !== index)
    }));
  };

  const updateFeature = (index, value) => {
    setFormData(prev => ({
      ...prev,
      features: prev.features.map((f, i) => i === index ? value : f)
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl font-bold text-dark-900">Services</h1>
          <p className="text-dark-500">
            {filteredServices.length} service{filteredServices.length > 1 ? 's' : ''} 
            {filteredServices.length !== services.length && ` sur ${services.length}`}
          </p>
        </div>
        <button onClick={() => openModal()} className="btn-primary">
          <Plus className="w-5 h-5 mr-2" />
          Nouveau service
        </button>
      </div>

      {/* Search & Filters Bar */}
      <div className="bg-white rounded-2xl shadow-sm border border-dark-100 p-4 mb-6">
        <div className="flex flex-col lg:flex-row gap-4">
          {/* Search */}
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-dark-400" />
            <input
              type="text"
              placeholder="Rechercher un service..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 bg-dark-50 border-0 rounded-xl focus:ring-2 focus:ring-primary-500 transition-all"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-dark-400 hover:text-dark-600"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          {/* Filter Toggle Button */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-xl border transition-all ${
              showFilters || activeFiltersCount > 0
                ? 'bg-primary-50 border-primary-200 text-primary-700'
                : 'bg-dark-50 border-transparent text-dark-600 hover:bg-dark-100'
            }`}
          >
            <Filter className="w-5 h-5" />
            Filtres
            {activeFiltersCount > 0 && (
              <span className="w-5 h-5 bg-primary-600 text-white text-xs rounded-full flex items-center justify-center">
                {activeFiltersCount}
              </span>
            )}
            <ChevronDown className={`w-4 h-4 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
          </button>

          {/* Reset Filters */}
          {activeFiltersCount > 0 && (
            <button
              onClick={resetFilters}
              className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-dark-600 hover:bg-dark-50 transition-all"
            >
              <RotateCcw className="w-4 h-4" />
              Réinitialiser
            </button>
          )}
        </div>

        {/* Expanded Filters */}
        <AnimatePresence>
          {showFilters && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="overflow-hidden"
            >
              <div className="grid sm:grid-cols-3 gap-4 pt-4 mt-4 border-t border-dark-100">
                {/* Status Filter */}
                <div>
                  <label className="block text-sm font-medium text-dark-600 mb-2">Statut</label>
                  <select
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    className="w-full px-3 py-2 bg-dark-50 border-0 rounded-xl focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="all">Tous les statuts</option>
                    <option value="active">Actifs</option>
                    <option value="inactive">Inactifs</option>
                  </select>
                </div>

                {/* Featured Filter */}
                <div>
                  <label className="block text-sm font-medium text-dark-600 mb-2">Mis en avant</label>
                  <select
                    value={filterFeatured}
                    onChange={(e) => setFilterFeatured(e.target.value)}
                    className="w-full px-3 py-2 bg-dark-50 border-0 rounded-xl focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="all">Tous</option>
                    <option value="featured">Mis en avant</option>
                    <option value="not_featured">Non mis en avant</option>
                  </select>
                </div>

                {/* Sort By */}
                <div>
                  <label className="block text-sm font-medium text-dark-600 mb-2">Trier par</label>
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="w-full px-3 py-2 bg-dark-50 border-0 rounded-xl focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="order">Ordre d'affichage</option>
                    <option value="title">Titre (A-Z)</option>
                    <option value="date">Date de création</option>
                  </select>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Services List */}
      {filteredServices.length === 0 ? (
        <div className="card p-12 text-center">
          {services.length === 0 ? (
            <>
              <Shield className="w-16 h-16 text-dark-300 mx-auto mb-4" />
              <p className="text-dark-500 mb-4">Aucun service créé</p>
              <button onClick={() => openModal()} className="btn-primary">
                Créer votre premier service
              </button>
            </>
          ) : (
            <>
              <Search className="w-16 h-16 text-dark-300 mx-auto mb-4" />
              <p className="text-dark-500 mb-4">Aucun service ne correspond à vos critères</p>
              <button onClick={resetFilters} className="btn-ghost">
                <RotateCcw className="w-4 h-4 mr-2" />
                Réinitialiser les filtres
              </button>
            </>
          )}
        </div>
      ) : (
        <div className="grid gap-4">
          {filteredServices.map((service, index) => {
            const IconComponent = iconMap[service.icon] || Shield;
            
            return (
              <motion.div
                key={service.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className={`card p-6 ${!service.is_active ? 'opacity-60' : ''}`}
              >
                <div className="flex items-start gap-4">
                  {/* Drag Handle & Icon */}
                  <div className="flex items-center gap-3">
                    <GripVertical className="w-5 h-5 text-dark-300 cursor-grab" />
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                      service.is_active ? 'bg-primary-100' : 'bg-dark-100'
                    }`}>
                      <IconComponent className={`w-6 h-6 ${service.is_active ? 'text-primary-600' : 'text-dark-400'}`} />
                    </div>
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <h3 className="font-semibold text-dark-900 flex items-center gap-2">
                          {service.title}
                          {service.is_featured && (
                            <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                          )}
                        </h3>
                        <p className="text-sm text-dark-500 mt-1 line-clamp-2">
                          {service.short_description}
                        </p>
                        {service.features && service.features.length > 0 && (
                          <p className="text-xs text-dark-400 mt-2">
                            {service.features.length} fonctionnalité{service.features.length > 1 ? 's' : ''}
                          </p>
                        )}
                      </div>

                      {/* Actions */}
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => toggleFeatured(service)}
                          className={`p-2 rounded-lg transition-colors ${
                            service.is_featured 
                              ? 'bg-yellow-100 text-yellow-600 hover:bg-yellow-200' 
                              : 'bg-dark-50 text-dark-400 hover:bg-dark-100'
                          }`}
                          title={service.is_featured ? 'Retirer de la une' : 'Mettre en avant'}
                        >
                          {service.is_featured ? <Star className="w-4 h-4 fill-current" /> : <StarOff className="w-4 h-4" />}
                        </button>
                        <button
                          onClick={() => toggleActive(service)}
                          className={`p-2 rounded-lg transition-colors ${
                            service.is_active 
                              ? 'bg-green-100 text-green-600 hover:bg-green-200' 
                              : 'bg-dark-50 text-dark-400 hover:bg-dark-100'
                          }`}
                          title={service.is_active ? 'Désactiver' : 'Activer'}
                        >
                          {service.is_active ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                        </button>
                        <button
                          onClick={() => openModal(service)}
                          className="p-2 bg-dark-50 text-dark-600 rounded-lg hover:bg-primary-50 hover:text-primary-600 transition-colors"
                          title="Modifier"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(service.id)}
                          className="p-2 bg-dark-50 text-dark-600 rounded-lg hover:bg-red-50 hover:text-red-600 transition-colors"
                          title="Supprimer"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      )}

      {/* Modal */}
      <AnimatePresence>
        {showModal && (
          <div className="fixed inset-0 bg-dark-900/50 flex items-center justify-center z-50 p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
            >
              <div className="p-6 border-b border-dark-100 flex items-center justify-between sticky top-0 bg-white z-10">
                <h2 className="text-xl font-semibold">
                  {editing ? 'Modifier le service' : 'Nouveau service'}
                </h2>
                <button onClick={() => setShowModal(false)} className="p-2 hover:bg-dark-100 rounded-lg">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="p-6 space-y-6">
                {/* Title */}
                <div>
                  <label className="label">Titre *</label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                    className="input"
                    required
                  />
                </div>

                {/* Short Description */}
                <div>
                  <label className="label">Description courte *</label>
                  <input
                    type="text"
                    value={formData.short_description}
                    onChange={(e) => setFormData(prev => ({ ...prev, short_description: e.target.value }))}
                    className="input"
                    required
                  />
                </div>

                {/* Icon */}
                <div>
                  <label className="label">Icône</label>
                  <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
                    {iconOptions.map((option) => {
                      const Icon = option.icon;
                      return (
                        <button
                          key={option.value}
                          type="button"
                          onClick={() => setFormData(prev => ({ ...prev, icon: option.value }))}
                          className={`p-3 rounded-xl border-2 flex flex-col items-center gap-1 transition-all ${
                            formData.icon === option.value
                              ? 'border-primary-500 bg-primary-50'
                              : 'border-dark-100 hover:border-dark-200'
                          }`}
                        >
                          <Icon className={`w-5 h-5 ${formData.icon === option.value ? 'text-primary-600' : 'text-dark-500'}`} />
                          <span className="text-xs">{option.label}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Description */}
                <div>
                  <label className="label">Description complète</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    className="input"
                    rows={4}
                  />
                </div>

                {/* Features */}
                <div>
                  <label className="label">Fonctionnalités</label>
                  <div className="space-y-2">
                    {formData.features.map((feature, index) => (
                      <div key={index} className="flex gap-2">
                        <input
                          type="text"
                          value={feature}
                          onChange={(e) => updateFeature(index, e.target.value)}
                          className="input flex-1"
                          placeholder={`Fonctionnalité ${index + 1}`}
                        />
                        {formData.features.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeFeature(index)}
                            className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                          >
                            <X className="w-5 h-5" />
                          </button>
                        )}
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={addFeature}
                      className="text-primary-600 text-sm font-medium hover:underline"
                    >
                      + Ajouter une fonctionnalité
                    </button>
                  </div>
                </div>

                {/* Options */}
                <div className="flex flex-wrap gap-6">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.is_active}
                      onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                      className="w-4 h-4 rounded border-dark-300 text-primary-600 focus:ring-primary-500"
                    />
                    <span className="text-dark-700">Actif</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.is_featured}
                      onChange={(e) => setFormData(prev => ({ ...prev, is_featured: e.target.checked }))}
                      className="w-4 h-4 rounded border-dark-300 text-primary-600 focus:ring-primary-500"
                    />
                    <span className="text-dark-700">Mettre en avant</span>
                  </label>
                </div>

                {/* Display Order */}
                <div>
                  <label className="label">Ordre d'affichage</label>
                  <input
                    type="number"
                    value={formData.display_order}
                    onChange={(e) => setFormData(prev => ({ ...prev, display_order: parseInt(e.target.value) || 0 }))}
                    className="input w-32"
                    min="0"
                  />
                </div>

                {/* Actions */}
                <div className="flex justify-end gap-3 pt-4 border-t border-dark-100">
                  <button type="button" onClick={() => setShowModal(false)} className="btn-ghost">
                    Annuler
                  </button>
                  <button type="submit" className="btn-primary">
                    <Save className="w-4 h-4 mr-2" />
                    {editing ? 'Enregistrer' : 'Créer'}
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default ServicesPage;